import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import type {
  Assessment,
  AssessmentDetail,
  AssessmentKind,
  AssessmentResults,
  AssessmentStatus,
  ChecklistItem,
  ChecklistTick,
  MaturityDescription,
  MaturityLevel,
  Pillar,
  PillarLevel,
  Question,
  Response,
  SeriesSummary,
  SeriesTrend,
} from '@internal/backstage-plugin-ai-gra-common';
import type { AiGraApi } from './aiGraApiRef';

export class AiGraApiClient implements AiGraApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  private async baseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('ai-gra');
  }

  private async fetch<T>(path: string, init?: RequestInit): Promise<T> {
    const base = await this.baseUrl();
    const res = await this.fetchApi.fetch(`${base}${path}`, init);
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Request failed (${res.status}): ${text}`);
    }
    if (res.status === 204) return undefined as unknown as T;
    return res.json();
  }

  getPillars(): Promise<Pillar[]> {
    return this.fetch('/pillars');
  }

  getMaturityDescriptions(): Promise<MaturityDescription[]> {
    return this.fetch('/maturity-descriptions');
  }

  getChecklistItems(): Promise<ChecklistItem[]> {
    return this.fetch('/checklist-items');
  }

  getQuestions(): Promise<Question[]> {
    return this.fetch('/questions');
  }

  listAssessments(kind?: AssessmentKind): Promise<Assessment[]> {
    const qs = kind ? `?kind=${kind}` : '';
    return this.fetch(`/assessments${qs}`);
  }

  createAssessment(data: {
    kind: AssessmentKind;
    organisationName: string;
  }): Promise<Assessment> {
    return this.fetch('/assessments', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  getAssessment(id: string): Promise<AssessmentDetail> {
    return this.fetch(`/assessments/${id}`);
  }

  patchAssessment(
    id: string,
    data: { organisationName?: string; status?: AssessmentStatus },
  ): Promise<Assessment> {
    return this.fetch(`/assessments/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  deleteAssessment(id: string): Promise<void> {
    return this.fetch(`/assessments/${id}`, { method: 'DELETE' });
  }

  upsertResponse(
    assessmentId: string,
    questionId: string,
    value: MaturityLevel,
  ): Promise<Response> {
    return this.fetch(`/assessments/${assessmentId}/responses/${questionId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ value }),
    });
  }

  getAssessmentResults(assessmentId: string): Promise<AssessmentResults> {
    return this.fetch(`/assessments/${assessmentId}/results`);
  }

  setPillarLevel(
    assessmentId: string,
    pillarId: string,
    level: MaturityLevel,
  ): Promise<PillarLevel> {
    return this.fetch(
      `/assessments/${assessmentId}/pillar-levels/${pillarId}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ level }),
      },
    );
  }

  setChecklistTick(
    assessmentId: string,
    itemId: string,
    checked: boolean,
  ): Promise<ChecklistTick | { checked: false }> {
    return this.fetch(
      `/assessments/${assessmentId}/checklist-ticks/${itemId}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ checked }),
      },
    );
  }

  listSeries(kind?: AssessmentKind): Promise<SeriesSummary[]> {
    const qs = kind ? `?kind=${kind}` : '';
    return this.fetch(`/assessments/series${qs}`);
  }

  getSeriesTrend(fingerprint: string): Promise<SeriesTrend> {
    return this.fetch(`/assessments/series/${fingerprint}/trend`);
  }
}
